import { useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import {
  useEnableFemaTenantMutation,
  useGetFemaTenantsQuery,
  useRotateFemaTenantMutation,
  useValidateFemaTenantMutation,
} from 'services/ipaws/tenants/femaTenants.api';
import {
  FEMA_TENANT_UNEXPECTED_ANSWER,
  toFemaTenantRefusal,
} from 'services/ipaws/tenants/femaTenants.service';
import CredentialExpiryBanner from './components/CredentialExpiryBanner/CredentialExpiryBanner';
import CredentialStatusCard from './components/CredentialStatusCard/CredentialStatusCard';
import { FEMA_CREDENTIALS_TITLE } from './config/femaCredentials.config';
import CredentialUploadWizard from './features/CredentialUploadWizard/index.web';
import { describeFemaTenantRefusal } from './utils/credentialState.utils';

/**
 * FE-11 — the client credential page (MULTICOG design read §3). One status card per held class
 * of the session workplace. A workplace without an IPAWS tenant gets an empty list, and the page
 * shows nothing. A malformed answer (`null` from the route reader) shows the contract refusal.
 */
const FemaCredentials = () => {
  const { data: tenants, isLoading, isError } = useGetFemaTenantsQuery();
  const [validateTenant, validateState] = useValidateFemaTenantMutation();
  const [enableTenant, enableState] = useEnableFemaTenantMutation();
  const [rotateTenant, rotateState] = useRotateFemaTenantMutation();
  const [wizard, setWizard] = useState(null);
  const [notice, setNotice] = useState(null);

  const busy = validateState.isLoading || enableState.isLoading || rotateState.isLoading;

  const run = async (command, success) => {
    setNotice(null);
    try {
      const answer = await command();
      if (answer === null) {
        setNotice({
          variant: 'danger',
          text: describeFemaTenantRefusal(FEMA_TENANT_UNEXPECTED_ANSWER),
        });
        return;
      }
      if (answer?.validation?.state === 'failed') {
        setNotice({
          variant: 'danger',
          text: `Validation failed. Stage: ${answer.validation.stage ?? 'not recorded'}.`,
        });
        return;
      }
      setNotice({ variant: 'success', text: success });
    } catch (failure) {
      setNotice({
        variant: 'danger',
        text: describeFemaTenantRefusal(toFemaTenantRefusal(failure)),
      });
    }
  };

  const onAction = (action, tenant) => {
    switch (action) {
      case 'upload':
        setWizard({ tenant, replace: false });
        return;
      case 'replace':
        // Rotation copy only when a current certificate keeps sending during the replacement.
        setWizard({ tenant, replace: Boolean(tenant.active && tenant.enabled) });
        return;
      case 'validate':
        run(
          () => validateTenant({ tenantId: tenant.id, slot: 'active' }).unwrap(),
          'Validation passed.',
        );
        return;
      case 'validateReplacement':
        run(
          () => validateTenant({ tenantId: tenant.id, slot: 'pending' }).unwrap(),
          'Validation of the replacement passed.',
        );
        return;
      case 'enable':
        run(() => enableTenant(tenant.id).unwrap(), 'The credential is enabled.');
        return;
      case 'rotate':
        run(() => rotateTenant(tenant.id).unwrap(), 'The replacement certificate is active.');
        return;
      default:
        return;
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2" role="status">
        <Spinner animation="border" size="sm" />
        <span>Loading the FEMA credentials.</span>
      </div>
    );
  }

  if (isError) {
    return <Alert variant="danger">The FEMA credentials did not load. Try again later.</Alert>;
  }

  if (tenants === null) {
    return (
      <Alert variant="danger" data-testid="fema-credentials-malformed">
        {describeFemaTenantRefusal(FEMA_TENANT_UNEXPECTED_ANSWER)}
      </Alert>
    );
  }

  if (!tenants || tenants.length === 0) return null;

  return (
    <div>
      <h1 className="h4 mb-3">{FEMA_CREDENTIALS_TITLE}</h1>
      {notice ? (
        <Alert variant={notice.variant} role={notice.variant === 'danger' ? 'alert' : 'status'}>
          {notice.text}
        </Alert>
      ) : null}
      {tenants.map((tenant) => (
        <div key={tenant.id}>
          <CredentialExpiryBanner tenant={tenant} />
          <CredentialStatusCard tenant={tenant} busy={busy} onAction={onAction} />
        </div>
      ))}
      {wizard ? (
        <CredentialUploadWizard
          tenant={wizard.tenant}
          replace={wizard.replace}
          onClose={() => setWizard(null)}
        />
      ) : null}
    </div>
  );
};

export default FemaCredentials;
